import { generatePath } from 'react-router-dom';
import { differenceInDays, differenceInHours, format } from 'date-fns';

import type {
  IAttachmentError,
  IResourceAnnouncementFolder,
  IResourceAnnouncementItem,
  IResourceDocumentFolder,
  IResourceFolder,
  IResourceItemType,
  IResourceDocumentItem,
  IResourceFolderTypes,
  IResourceLinkFolder,
  IResourceLinkItem,
  IResourceFolderType,
  IResourceDatabaseFolder,
  IResourceDatabaseView,
  IResourceAnnouncementItemWithClient,
  IResourceItem,
  IResourceDatabaseField,
  IFolderItemType,
  IResourceDatabaseRecord,
  IResourceItemWithClientType,
} from '@/api/types';
import type { IDateFormatter } from '@/types/date';
import type {
  ICommonItemColumnNames,
  ICommonFolderColumnNames,
  IResourceType,
  ITreeFolder,
  IFolderType,
  IBasicClientFolder,
  IBasicFolder,
  IDatabaseFolderColumnNames,
  IDatabaseFieldColumnNames,
  IDatabaseViewColumnNames,
} from '@/components/common';
import type { ISortingFunctions } from '@/components/common/table/types';
import type { IBreadcrumbPathItem, ISelectedFolder } from '@/store/types';
import type { IFileProxy } from '@/components/common/form/types';

import routes from '@/config/routes';
import { findNestedObj, findParentObj, isOfType } from './other';
import { AnnouncementType, ResourceTypeId, ResourceVisibility } from '@/store/enums';
import {
  AM_PM_TIME_SPACE_FORMAT,
  ATTACHMENT_PREFIX_REGEX,
  MONTH_DAY_YEAR_FORMAT,
  MONTH_DAY_YEAR_HOUR_FORMAT,
  NEW_RESOURCE_THRESHOLD,
  PENDING_ALERT_THRESHHOLD,
} from '@/config/variables';
import { ResourceDatabaseFieldTypeId } from '@/api/enums';

const filterFolders = (array: IResourceFolderTypes, folderName?: string) => {
  if (!folderName || array.length === 0) return array;
  return array.reduce<IResourceFolderTypes>((r, o) => {
    const folders = filterFolders(o.folders || [], folderName);
    if (folders.length) {
      r.push(Object.assign({}, o, { folders }));
    }
    if (o.name.toLowerCase().indexOf(folderName.toLowerCase()) !== -1) {
      r.push(o);
      if (o.folders && o.name.toLowerCase().indexOf(folderName.toLowerCase()) === -1) {
        r[0].folders = [];
      }
    }
    return r;
  }, []);
};

const compareVisibility = (
  a: IResourceItemType | IResourceFolderType | IResourceDatabaseFolder | IResourceDatabaseView,
  b: IResourceItemType | IResourceFolderType | IResourceDatabaseFolder | IResourceDatabaseView,
): number => {
  if (
    isResourceDatabaseFolder(a) ||
    isResourceDatabaseFolder(b) ||
    isResourceDatabaseView(a) ||
    isResourceDatabaseView(b)
  ) {
    return 0;
  }

  if (a.publicationStatus === b.publicationStatus) {
    return 0;
  }

  if (a.publicationStatus === undefined) {
    return -1;
  }

  if (b.publicationStatus === undefined) {
    return 1;
  }

  return a.publicationStatus - b.publicationStatus;
};

const commonItemSortingFunctions: ISortingFunctions<ICommonItemColumnNames, IResourceItemType | IResourceDatabaseView> =
  {
    name: (a, b) => getLabel(a).localeCompare(getLabel(b)),
    dateEnd: (a, b) => {
      if (isResourceDatabaseView(a) || isResourceDatabaseView(b)) return 0;

      if (!a.dateEnd && !b.dateEnd) {
        return 0;
      }

      if (!a.dateEnd) {
        return -1;
      }

      if (!b.dateEnd) {
        return 1;
      }
      return new Date(a.dateEnd).valueOf() - new Date(b.dateEnd).valueOf();
    },
    dateStart: (a, b) =>
      new Date((a as IResourceItemType).dateStart).valueOf() - new Date((b as IResourceItemType).dateStart).valueOf(),
    visibility: compareVisibility,
    createdByName: (a, b) => {
      if (isResourceDatabaseView(a) || isResourceDatabaseView(b)) return 0;
      return a.createdByName.localeCompare(b.createdByName);
    },
    modifiedByName: (a, b) => {
      if (isResourceDatabaseView(a) || isResourceDatabaseView(b)) return 0;
      return a.createdByName.localeCompare(b.createdByName);
    },
  };

const databaseFolderSortingFunctions: ISortingFunctions<IDatabaseFolderColumnNames, IResourceDatabaseFolder> = {
  name: (a, b) => getLabel(a).localeCompare(getLabel(b)),
  status: (a, b) => {
    if (!a.status && !b.status) {
      return 0;
    }

    if (!a.status) {
      return -1;
    }

    if (!b.status) {
      return 1;
    }

    return a.status - b.status;
  },
  numFields: (a, b) => a.numFields - b.numFields,
  numViews: (a, b) => a.numViews - b.numViews,
  numRecords: (a, b) => a.numRecords - b.numRecords,
};

const databaseFieldSortingFunctions: ISortingFunctions<IDatabaseFieldColumnNames, IResourceDatabaseField> = {
  name: (a, b) => a.name.localeCompare(b.name),
  type: (a, b) => {
    if (!a.type && !b.type) {
      return 0;
    }

    if (!a.type) {
      return -1;
    }

    if (!b.type) {
      return 1;
    }

    return a.type.localeCompare(b.type);
  },
  searchable: (a, b) => +a.searchable - +b.searchable,
  required: (a, b) => +a.required - +b.required,
  unique: (a, b) => +a.unique - +b.unique,
};

const databaseViewSortingFunctions: ISortingFunctions<IDatabaseViewColumnNames, IResourceDatabaseView> = {
  name: (a, b) => a.name.localeCompare(b.name),
  viewAccess: (a, b) => a.viewAccess - b.viewAccess,
};

const commonFolderSortingFunctions: ISortingFunctions<
  ICommonFolderColumnNames,
  IResourceFolderType | IResourceDatabaseFolder
> = {
  name: (a, b) => getLabel(a).localeCompare(getLabel(b)),
  visibility: compareVisibility,
  items: (a, b) => getFolderChildren(a).length - getFolderChildren(b).length,
  description: (a, b) => getLabel(a).localeCompare(getLabel(b)),
  agentVisible: (a, b) =>
    Number((a as IResourceFolderType)?.agentVisible) - Number((b as IResourceFolderType)?.agentVisible),
};

const getFolderName = (folders: IResourceFolderTypes, searchTerm = '') => {
  if (!folders.length) return '';

  const options = {
    announcements: { itemNameKey: 'title' },
    docs: { itemNameKey: 'name' },
    links: { itemNameKey: 'label' },
  };

  const folderType = getFolderResourcesType(folders[0]) as keyof typeof options;
  const obj = findNestedObj(folders, options[folderType].itemNameKey, searchTerm, true);
  const parentFolder = (findParentObj(folders, folderType, obj) || {}) as IResourceFolder;
  const path = (getPath(folders, parentFolder?.folderId) || []) as string[];
  const parentFolderName = path[path.length - 1];
  return parentFolderName || searchTerm;
};

const filterTree = (tree: ITreeFolder[], searchTerm?: string): ITreeFolder[] => {
  if (!searchTerm) {
    return tree;
  }

  return tree.flatMap(clientTree => {
    const filteredAnnouncements = filterFolders(
      clientTree.announcement,
      getFolderName(clientTree.announcement, searchTerm),
    ) as IResourceAnnouncementFolder[];
    const filteredDocuments = filterFolders(
      clientTree.document,
      getFolderName(clientTree.document, searchTerm),
    ) as IResourceDocumentFolder[];
    const filteredLinks = filterFolders(
      clientTree.link,
      getFolderName(clientTree.link, searchTerm),
    ) as IResourceLinkFolder[];

    const hasNoResources = !filteredAnnouncements.length && !filteredDocuments.length && !filteredLinks.length;
    const matchClientName =
      searchTerm &&
      (clientTree.clientName.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1 ||
        clientTree.clientShortName.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1);

    if (hasNoResources) {
      return matchClientName ? clientTree : [];
    }

    return {
      ...clientTree,
      announcement: filteredAnnouncements,
      document: filteredDocuments,
      link: filteredLinks,
    };
  });
};

const getPath = (arr: IResourceFolderTypes, folderId: number): string[] | undefined => {
  for (const child of arr) {
    if (child.folderId === folderId) {
      return [child.name];
    } else if (isOfType<IResourceDocumentFolder>(child, 'folders') && !!child.folders?.length) {
      const path = getPath(child.folders, folderId);
      if (path) return Array.isArray(path) ? [child.name, ...path] : [child.name, path];
    }
  }
  return undefined;
};

const getBreadcrumbPath = (selectedFolder: ISelectedFolder, tree: ITreeFolder[]): IBreadcrumbPathItem[] | undefined => {
  if (selectedFolder.folderType === 'home') return undefined;

  const clientFolder = tree.find(client => client.clientId === selectedFolder.clientId);
  if (!clientFolder) return undefined;

  const path: IBreadcrumbPathItem[] = [
    {
      clientId: clientFolder.clientId,
      folderId: clientFolder.clientId,
      folderType: 'client' as IFolderType,
      name: clientFolder.clientName,
    },
  ];

  if (selectedFolder.folderType === 'client') {
    return path;
  }

  if (selectedFolder.folderType === 'category') {
    const resourceType = ResourceTypeId[selectedFolder.folderId] as IResourceType;
    return [...path, { ...selectedFolder, name: itemTranslationMap[resourceType] }];
  }

  const folderType = selectedFolder.folderType === 'view' ? 'database' : selectedFolder.folderType;

  path.push({
    clientId: clientFolder.clientId,
    folderId: ResourceTypeId[folderType],
    folderType: 'category' as IFolderType,
    name: itemTranslationMap[folderType],
  });

  const categoryFolder = clientFolder[folderType];
  if (!categoryFolder) return undefined;

  let parentFolder = categoryFolder;

  while (true) {
    const subFolder = (parentFolder as any[]).find(folder =>
      findNestedObj(folder, 'folderId', selectedFolder.folderId),
    ) as IResourceFolderType | IResourceDatabaseFolder;

    if (!subFolder) return undefined;

    path.push({
      clientId: clientFolder.clientId,
      folderId: subFolder.folderId,
      folderType: folderType,
      name: subFolder.name,
    });

    if (subFolder.folderId === selectedFolder.folderId) {
      if (selectedFolder.folderType === 'view') {
        const view = (subFolder.folders as IResourceDatabaseView[])?.find(view => view.id === selectedFolder.viewId);
        if (!view) return path;

        path.push({
          clientId: clientFolder.clientId,
          folderId: subFolder.folderId,
          viewId: view.folderId,
          folderType: 'view',
          name: view.name,
        });
      }
      return path;
    }

    if (subFolder?.folders?.length) {
      parentFolder = subFolder.folders as never[] | IResourceDocumentFolder[];
    } else {
      return undefined;
    }
  }
};

const paginate = <T>(arr: T[], size: number): T[][] => {
  const paginated = arr.reduce<T[][]>((acc, val, i) => {
    const idx = Math.floor(i / size);
    const page = acc[idx] || (acc[idx] = []);
    page.push(val);
    return acc;
  }, []);

  return paginated;
};

const isResource = (value: string): value is IResourceType =>
  ['announcement', 'document', 'link', 'database'].includes(value);

const isResourceAnnouncementItem = (data: unknown): data is IResourceAnnouncementItem =>
  isOfType<IResourceAnnouncementItem>(data, 'announcementId');

const isResourceAnnouncementFolder = (data: unknown): data is IResourceAnnouncementFolder =>
  isOfType<IResourceAnnouncementFolder>(data, 'announcements');

const isResourceDocumentItem = (data: unknown): data is IResourceDocumentItem =>
  isOfType<IResourceDocumentItem>(data, 'documentId');

const isResourceDocumentFolder = (data: unknown): data is IResourceDocumentFolder =>
  isOfType<IResourceDocumentFolder>(data, 'docs');

const isResourceLinkItem = (data: unknown): data is IResourceLinkItem => isOfType<IResourceLinkItem>(data, 'linkId');

const isResourceLinkFolder = (data: unknown): data is IResourceLinkFolder =>
  isOfType<IResourceLinkFolder>(data, 'links');

const isResourceDatabaseFolder = (data: unknown): data is IResourceDatabaseFolder =>
  isOfType<IResourceDatabaseFolder>(data, 'default_view_id');

const isResourceDatabaseView = (data: unknown): data is IResourceDatabaseView =>
  isOfType<IResourceDatabaseView>(data, 'rtxDatabase_Id');

const isSimpleResourceFolder = (
  data: unknown,
): data is IResourceLinkFolder | IResourceAnnouncementFolder | IResourceDocumentFolder =>
  isResourceAnnouncementFolder(data) || isResourceDocumentFolder(data) || isResourceLinkFolder(data);

const getResourceItemType = (resource: IResourceItemType | IResourceDatabaseRecord): IResourceType => {
  if (isResourceAnnouncementItem(resource)) return 'announcement';
  if (isResourceDocumentItem(resource)) return 'document';
  if (isResourceLinkItem(resource)) return 'link';
  return 'database';
};

const getFolderResourcesType = (folder: IResourceFolderType | IResourceDatabaseView): string | undefined => {
  if (isResourceAnnouncementFolder(folder)) return 'announcements';
  if (isResourceDocumentFolder(folder)) return 'docs';
  if (isResourceLinkFolder(folder)) return 'links';
  if (isResourceDatabaseFolder(folder)) return 'folders';
  if (isResourceDatabaseView(folder)) return 'data';
  return undefined;
};

const getResourceItemId = (resource: IResourceItemType | IResourceDatabaseRecord): number => {
  if (isResourceAnnouncementItem(resource)) return resource.announcementId;
  if (isResourceDocumentItem(resource)) return resource.documentId;
  if (isResourceLinkItem(resource)) return resource.linkId;
  return resource['Id']; // database view item id
};

const isAttachementError = (data: unknown): data is IAttachmentError => isOfType<IAttachmentError>(data, 'error');

const getLabel = (
  data: IResourceItemType | IResourceFolderType | IResourceDatabaseFolder | IResourceDatabaseView,
): string => {
  if (isResourceAnnouncementItem(data)) return data.title;
  if (isResourceDocumentItem(data)) return data.name;
  if (isResourceLinkItem(data)) return data.label;
  if (isResourceDatabaseFolder(data)) return data.name;
  if (isResourceDatabaseView(data)) return data.name;
  if (isOfType<IResourceFolderType>(data, 'folderId')) return data.name;
  return '';
};

const getFolderChildren = (
  data: IResourceFolderType | IResourceDatabaseFolder,
): IResourceDocumentItem[] | IResourceAnnouncementItem[] | IResourceLinkItem[] | IResourceDatabaseView[] => {
  if (isResourceAnnouncementFolder(data)) return data.announcements;
  if (isResourceDocumentFolder(data)) return data.docs;
  if (isResourceLinkFolder(data)) return data.links;
  if (isResourceDatabaseFolder(data)) return data.folders || [];
  return [];
};

const getDescription = (data: IResourceItemType | IResourceDatabaseFolder): string => {
  if (isResourceAnnouncementItem(data)) return data.body || data.bodyShort;
  if (isResourceDocumentItem(data)) return data.description || data.shortDescription;
  if (isResourceLinkItem(data)) return data.description;
  if (isResourceDatabaseFolder(data)) return data.description;
  return '';
};

const getResourceTypeId = (
  resourceItem: IResourceItemType | IResourceDatabaseRecord,
): { type: IResourceType; id: number } => ({
  type: getResourceItemType(resourceItem),
  id: getResourceItemId(resourceItem),
});

const getNewTabLink = (resource: IResourceItemWithClientType): string => {
  const { type, id } = getResourceTypeId(resource);
  return `${location.origin}${generatePath(`${routes.sp.scheduling.resources.preview}`, {
    resourceType: type,
    id,
    client: resource.clientId,
  })}`;
};

const getNewTabDatabaseViewItemLink = (dbId: number, viewId: number, id: number, clientId: number): string => {
  const params = {
    dbId,
    viewId,
    id,
    client: clientId,
  };
  return `${location.origin}${generatePath(`${routes.sp.scheduling.resources.previewDatabaseItem}`, params)}`;
};

const itemTranslationMap: { [key in IResourceType]: string } = {
  announcement: 'resources.content.announcements',
  document: 'resources.content.documents',
  link: 'resources.content.links',
  database: 'resources.content.databases',
};

const addItemTranslationMap: { [key in Exclude<IFolderType, 'home'>]: string } = {
  announcement: 'resources.content.addAnnouncement',
  document: 'resources.content.addDocument',
  link: 'resources.content.addLink',
  database: 'resources.content.addDatabase',
  client: 'resources.content.addFolder',
  category: 'resources.content.addFolder',
  view: 'resources.content.addView',
};

const getResourceVisibility = (resource: IResourceItemType): ResourceVisibility => {
  const now = Date.now();
  const start = new Date(resource.dateStart).getTime();

  if (now < start) {
    return ResourceVisibility.unpublished;
  }

  if (!resource.dateEnd) {
    return ResourceVisibility.published;
  }

  const end = new Date(resource.dateEnd).getTime();

  if (now > end) {
    return ResourceVisibility.expired;
  }

  return ResourceVisibility.published;
};

const getNestedItems = <T extends IResourceFolderType>(resourceFolder: T): IFolderItemType<T>[] => {
  const items = [...(getFolderChildren(resourceFolder) as IFolderItemType<T>[])];

  if (resourceFolder.folders?.length) {
    const newItems = (resourceFolder.folders as T[]).flatMap<IFolderItemType<T>>(folder => getNestedItems(folder));
    items.push(...newItems);
  }

  return items;
};

const getRecentAnnouncements = (
  announcementFolders: IResourceAnnouncementFolder[] | ITreeFolder[],
  limit: number,
): IResourceAnnouncementItem[] => {
  if (announcementFolders.length === 0) return [];

  // for specific client
  if (isResourceAnnouncementFolder(announcementFolders[0])) {
    return (announcementFolders as IResourceAnnouncementFolder[])
      .flatMap(getNestedItems)
      .sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime())
      .slice(0, limit);
  }

  // all
  return (announcementFolders as ITreeFolder[])
    .flatMap(client => client.announcement.flatMap(getNestedItems))
    .sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime())
    .slice(0, limit);
};

const mapToResourceWithClient =
  <T extends IResourceItem>(
    client: ITreeFolder,
  ): ((resource: T) => T & {
    clientId: number;
    clientName: string;
    clientShortName: string;
  }) =>
  (resource: T) => ({
    ...resource,
    clientId: client.clientId,
    clientName: client.clientName,
    clientShortName: client.clientShortName,
  });

const getRecentlyPublishedAnnouncements = (
  announcementFolders: ITreeFolder[],
  shippingCountry: number,
): IResourceAnnouncementItemWithClient[] =>
  announcementFolders
    .flatMap(client =>
      client.announcement
        .flatMap(getNestedItems)
        .map<IResourceAnnouncementItemWithClient>(mapToResourceWithClient(client)),
    )
    .filter(
      announcement =>
        (isNewResource(announcement) || announcement.dateEnd === null) &&
        announcement.countries !== null &&
        announcement.countries.indexOf(shippingCountry) !== -1 &&
        !isActiveAlert(announcement),
    )
    .sort((a, b) => new Date(b.dateStart).getTime() - new Date(a.dateStart).getTime());

const getRecentLinks = (linkFolder: IResourceLinkFolder[], limit: number): IResourceLinkItem[] =>
  linkFolder
    .flatMap(getNestedItems)
    .sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime())
    .slice(0, limit);

const getRecentDocuments = (documentFolder: IResourceDocumentFolder[], limit: number): IResourceDocumentItem[] =>
  documentFolder
    .flatMap(getNestedItems)
    .sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime())
    .slice(0, limit);

const getPinnedAnnouncements = (folder: ITreeFolder[]): IResourceAnnouncementItemWithClient[] =>
  folder
    .flatMap(client =>
      client.announcement
        .flatMap(getNestedItems)
        .map<IResourceAnnouncementItemWithClient>(mapToResourceWithClient(client))
        .filter(announcement => announcement.stayOnTop && !isActiveAlert(announcement)),
    )
    .sort((a, b) => new Date(b.dateStart).getTime() - new Date(a.dateStart).getTime());

const getPendingAlerts = (folder: ITreeFolder[]): IResourceAnnouncementItemWithClient[] =>
  folder
    .flatMap(client =>
      client.announcement
        .flatMap(getNestedItems)
        .map<IResourceAnnouncementItemWithClient>(mapToResourceWithClient(client))
        .filter(isActiveAlert),
    )
    .sort((a, b) => new Date(b.dateStart).getTime() - new Date(a.dateStart).getTime());

const getSelectedFolderName = (foldersList: ITreeFolder[], selectedFolder: ISelectedFolder): string => {
  switch (selectedFolder.folderType) {
    case 'client':
      const client = foldersList.find(client => client.clientId);
      if (!client) return '';

      return client.clientName;
    case 'category':
      return itemTranslationMap[ResourceTypeId[selectedFolder.folderId] as IResourceType];
    case 'announcement':
    case 'document':
    case 'link':
    case 'database': {
      const selectedClientFolder = foldersList.find(clientFolder => clientFolder.clientId === selectedFolder.clientId);
      if (!selectedClientFolder) return '';

      const folder = findNestedObj(
        selectedClientFolder[selectedFolder.folderType],
        'folderId',
        selectedFolder.folderId,
      );
      if (!folder) return '';

      return folder.name;
    }
    case 'view': {
      const selectedClientFolder = foldersList.find(clientFolder => clientFolder.clientId === selectedFolder.clientId);
      if (!selectedClientFolder) return '';
      const db = selectedClientFolder.database.find(db => db.id === selectedFolder.folderId);
      const view = db?.folders?.find(view => view.id === selectedFolder.viewId);

      return view?.name || '';
    }
    default:
      return '';
  }
};

const getFolderType = (folder: unknown): IFolderType => {
  if (isResourceAnnouncementFolder(folder)) return 'announcement';
  if (isResourceLinkFolder(folder)) return 'link';
  if (isResourceDocumentFolder(folder)) return 'document';
  if (isResourceDatabaseFolder(folder)) return 'database';
  if (isResourceDatabaseView(folder)) return 'database';
  if (isOfType<ITreeFolder>(folder, 'clientName')) return 'client';

  return 'category';
};

const compareSelectedFolder = (selectedFolder: ISelectedFolder, folderToCompare: ISelectedFolder): boolean =>
  folderToCompare.folderId === selectedFolder.folderId &&
  folderToCompare.clientId === selectedFolder.clientId &&
  folderToCompare.folderType === selectedFolder.folderType &&
  (!!selectedFolder.viewId ? selectedFolder.viewId === folderToCompare.viewId : true);

const isFolderInside = (
  parentFolder: ISelectedFolder,
  selectedFolder: ISelectedFolder,
  foldersList: ITreeFolder[],
): boolean => {
  const clientFolder = foldersList.find(clientFolder => clientFolder.clientId === parentFolder.clientId);
  if (!clientFolder || !isResource(parentFolder.folderType)) {
    return false;
  }
  const categoryFolder = clientFolder[parentFolder.folderType];
  const parent = findNestedObj(categoryFolder, 'folderId', parentFolder.folderId);

  if (!parent) return false;

  return !!findNestedObj([parent], 'folderId', selectedFolder.folderId);
};

const isChildrenSelected = (
  parentFolder: ISelectedFolder,
  selectedFolder: ISelectedFolder,
  foldersList: ITreeFolder[],
): boolean => {
  if (parentFolder.clientId !== selectedFolder.clientId || parentFolder.folderType === 'home') return false;

  switch (selectedFolder.folderType) {
    case 'client':
      return false;
    case 'category':
      return parentFolder.folderType === 'client';
    case 'announcement':
    case 'document':
    case 'link': {
      if (parentFolder.folderType === 'client') return true;

      if (parentFolder.folderType === 'category')
        return parentFolder.folderId === ResourceTypeId[selectedFolder.folderType];

      if (['announcement', 'document', 'link'].includes(parentFolder.folderType))
        return isFolderInside(parentFolder, selectedFolder, foldersList);

      return false;
    }
    case 'database': {
      if (parentFolder.folderType === 'client') return true;

      if (parentFolder.folderType === 'category') return parentFolder.folderId === ResourceTypeId.database;

      return false;
    }
    case 'view': {
      if (parentFolder.folderType === 'client') return true;

      if (parentFolder.folderType === 'category') return parentFolder.folderId === ResourceTypeId.database;

      if (parentFolder.folderType === 'database') return isFolderInside(parentFolder, selectedFolder, foldersList);

      return false;
    }
    default:
      return false;
  }
};

const isNewResource = (item: IResourceItemType): boolean =>
  differenceInDays(new Date(), new Date(item.dateStart)) < NEW_RESOURCE_THRESHOLD;

const isActiveAlert = (announcement: IResourceAnnouncementItem): boolean =>
  announcement.type === AnnouncementType.alert &&
  differenceInHours(new Date(), new Date(announcement.dateStart)) < PENDING_ALERT_THRESHHOLD;

const mapFolderToBasicFolder = (
  folder: Omit<IResourceFolderType, 'IResourceDatabaseFolder'>,
  clientId: number,
): IBasicFolder => {
  if (!folder.folders) {
    return {
      name: folder.name,
      id: folder.folderId,
      clientId,
      children: [],
    };
  }

  return {
    name: folder.name,
    id: folder.folderId,
    clientId,
    children: folder.folders.map(folder => mapFolderToBasicFolder(folder, clientId)),
  };
};

const mapFolderTreeToBasicClientFolder = (
  foldersList: ITreeFolder[],
  type: IResourceType,
  clientId?: number,
): IBasicClientFolder[] => {
  return foldersList
    .filter(clientTree => !clientId || clientTree.clientId === clientId)
    .map(folderTree => {
      const resourceFolder = folderTree[type] as Omit<IResourceFolderType, 'IResourceDatabaseFolder'>[];
      const basicResourceFolders = resourceFolder.map(folder => mapFolderToBasicFolder(folder, folderTree.clientId));

      return { clientId: folderTree.clientId, clientName: folderTree.clientName, children: basicResourceFolders };
    });
};

const getSelectedFolderId = (selectedFolder: ISelectedFolder): number | null => {
  if (['document', 'announcement', 'link'].includes(selectedFolder.folderType)) {
    return selectedFolder.folderId;
  }

  return null;
};

const createFileProxyFromPath = (path?: string | null): IFileProxy[] => {
  if (!path) {
    return [];
  }

  let fileName = null;
  try {
    fileName = new URL(path).pathname.split('/').pop();
  } catch (error) {}

  const proxy = fileName ? [{ file: null, name: decodeURI(fileName), path }] : [];

  return proxy;
};

const getDateFromTime = (time: string): number => {
  const truncated = time.substring(0, time.indexOf('.'));
  const [hours, minutes, seconds] = truncated.split(':').map(Number);
  const date = new Date().setHours(hours, minutes, seconds);

  return date;
};

const getFileNameFromFileUrl = (url: string): string => {
  const fileName = url.substring(url.lastIndexOf('/') + 1);
  const displayName = fileName.replace(ATTACHMENT_PREFIX_REGEX, '');

  return displayName;
};

const formatValueForExport = (
  value: string | number | null,
  formatDateTime: IDateFormatter,
  field?: IResourceDatabaseField,
): string => {
  if (!field) return '';

  switch (field.rtxtype_Id) {
    case ResourceDatabaseFieldTypeId.Time: {
      const time = value as string;
      const date = getDateFromTime(time);
      return format(date, AM_PM_TIME_SPACE_FORMAT);
    }
    case ResourceDatabaseFieldTypeId.Date: {
      const date = new Date(value as string);
      return format(date, MONTH_DAY_YEAR_FORMAT);
    }
    case ResourceDatabaseFieldTypeId.DateTime: {
      const date = value as string;
      return formatDateTime(date, MONTH_DAY_YEAR_HOUR_FORMAT);
    }
    case ResourceDatabaseFieldTypeId.MemberList: {
      if (!value) return '';
      const [, name] = (value as string).split('|');
      return name;
    }
    case ResourceDatabaseFieldTypeId.Currency: {
      if (!value) return '';
      return `$${value}`;
    }
    case ResourceDatabaseFieldTypeId.FileAttachment: {
      if (!value) return '';
      return encodeURI(value.toString());
    }
    default:
      if (!value) return '';
      return value.toString().replaceAll('"', '""');
  }
};

const exportViewData = (
  data: IResourceDatabaseRecord[],
  view: IResourceDatabaseView,
  formatDateTime: IDateFormatter,
): Blob | null => {
  if (data.length === 0) return null;

  const fields = view.props.map<[number, string]>(prop => [
    prop.field.id,
    // this is required for legacy reasons
    prop.field.name === 'Unique Id' ? 'Id' : prop.field.name,
  ]);
  const header = fields.map(([, name]) => `"${name}"`).join(',');
  const rows = data.map(row =>
    fields
      .map(
        ([id, name]) =>
          `"${formatValueForExport(row[name], formatDateTime, view.props.find(prop => prop.field.id === id)?.field)}"`,
      )
      .join(','),
  );
  const csvContent = [header, ...rows].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });

  return blob;
};

export {
  filterTree,
  getFolderResourcesType,
  getPath,
  getBreadcrumbPath,
  isResourceAnnouncementItem,
  isResourceDocumentItem,
  isResourceLinkItem,
  isAttachementError,
  getLabel,
  getDescription,
  paginate,
  commonItemSortingFunctions,
  databaseFolderSortingFunctions,
  commonFolderSortingFunctions,
  databaseFieldSortingFunctions,
  databaseViewSortingFunctions,
  getNewTabLink,
  getNewTabDatabaseViewItemLink,
  getResourceItemType,
  getResourceItemId,
  getResourceTypeId,
  itemTranslationMap,
  addItemTranslationMap,
  getResourceVisibility,
  getRecentAnnouncements,
  getRecentLinks,
  getRecentDocuments,
  isResourceAnnouncementFolder,
  isResourceDocumentFolder,
  isResourceLinkFolder,
  isResourceDatabaseFolder,
  isResourceDatabaseView,
  isSimpleResourceFolder,
  getPinnedAnnouncements,
  getFolderChildren,
  getSelectedFolderName,
  getFolderType,
  compareSelectedFolder,
  isChildrenSelected,
  isFolderInside,
  isNewResource,
  mapToResourceWithClient,
  getRecentlyPublishedAnnouncements,
  isResource,
  mapFolderTreeToBasicClientFolder,
  getSelectedFolderId,
  createFileProxyFromPath,
  getPendingAlerts,
  getDateFromTime,
  getFileNameFromFileUrl,
  exportViewData,
};
